import { OAuthProvider } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { ComponentProps, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { useData } from '../../contexts/data-context/use-data';
import { ErrorSection } from '../../design-system/error-section/error-section';
import { LoadingSection } from '../../design-system/loading/loading';
import { requiresOAuthReconnection } from '../../utils/error-utils';
import { exhaustiveCheck } from '../../utils/exhaustive-check';
import { postSwrFetcher } from '../../utils/post-swr-fetcher';
import { CONNECT_XERO_URL } from '../invoice-pay/components/connections-and-settings-section';
import { OAuthProviderDisconnectConfirmationModal } from '../invoice-pay/components/invoice-data-section/modals/oauth-provider-disconnect-confirmation-modal';
import { XeroConnectModal } from '../invoice-pay/components/invoice-data-section/modals/xero/xero-connect-modal';
import { useGmailConnection } from '../invoice-pay/utils/use-gmail-connection';
import { useXeroConnection } from '../invoice-pay/utils/use-xero-connection';
import { AccountMappingExitConfirmationModal } from './components/xero-flow-modal/account-mapping-exit-confirmation-modal';
import { GetBankAccountsResponse } from './components/xero-flow-modal/account-mapping-form';
import { AccountMappingModal } from './components/xero-flow-modal/account-mapping-modal';
import { IntegrationsPage } from './integrations-page';
import { OAuthConnectionInformation, OAuthConnectionStatus, OAuthProviderConnection } from './types/types';
import { isAllowedOAuthProvider } from './utils/is-allowed-oauth-provider';

const API_URL_ACCOUNTING = '/api/accounting/';
const XERO_CONNECTION_URL = '/integrations/xero';

export type BankAccountMapping = {
  seapointAccountId: string; // Includes Modulr and OB accounts
  externalAccountId: string; // Xero bank accounts
  externalAccountName?: string;
  currency?: string;
  syncFromDate?: string;
};

export const IntegrationsPageWrapper = () => {
  const navigate = useNavigate();
  const { oauthProvider } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isXeroProvider = searchParams.get('provider') === 'xero';
  const isXeroSuccess = searchParams.get('success') === 'true';
  const preselectedAccountId = searchParams.get('accountId'); // For reconnect flow
  const { data, isLoading: isLoadingData, error: dataError } = useData();

  const {
    xeroConnectionStatus,
    isCheckingXeroConnection,
    isDisconnectingXero,
    mutations: { onDisconnectXero },
  } = useXeroConnection();
  const {
    gmailConnectionStatus,
    isCheckingGmailConnection,
    isDisconnectingGmail,
    mutations: { onDisconnectGmail },
  } = useGmailConnection();

  // Only fetch Xero details when on Xero settings page or account mapping modal
  // This reduces Xero API calls on the main integrations page
  const shouldFetchXeroDetails =
    (oauthProvider === OAuthProvider.XERO || isXeroProvider) && xeroConnectionStatus?.connectionId;

  const {
    data: xeroBankAccountsResponse,
    isLoading: isLoadingXeroBankAccounts,
    error: xeroBankAccountsError,
    mutate: mutateXeroBankAccounts,
  } = useSWR<GetBankAccountsResponse, Error, string | null>(
    shouldFetchXeroDetails ? `${API_URL_ACCOUNTING}${xeroConnectionStatus?.connectionId}/bank-accounts` : null,
  );

  // Extract accounts and rate limit info from response
  const xeroBankAccounts = xeroBankAccountsResponse?.accounts;
  const isXeroBankAccountsRateLimited = xeroBankAccountsResponse?.isRateLimited;
  const {
    data: xeroAccountMappings,
    isLoading: isLoadingXeroAccountMappings,
    error: xeroAccountMappingsError,
  } = useSWR<{ mappings: BankAccountMapping[] }, Error, string | null>(
    shouldFetchXeroDetails ? `${API_URL_ACCOUNTING}${xeroConnectionStatus?.connectionId}/account-mappings` : null,
  );
  const { trigger: triggerXeroAccountMappings, isMutating: isMutatingXeroAccountMappings } = useSWRMutation<
    { xeroAccountId: string; message: string },
    Error,
    string | null,
    { mappings: BankAccountMapping[] }
  >(
    xeroConnectionStatus?.connectionId
      ? `${API_URL_ACCOUNTING}${xeroConnectionStatus?.connectionId}/account-mappings`
      : null,
    postSwrFetcher,
  );
  const { trigger: triggerCreateXeroBankAccount, isMutating: isMutatingCreateXeroBankAccount } = useSWRMutation<
    { xeroAccountId: string; xeroAccountName: string; xeroAccountCurrency: string; message: string },
    Error,
    string | null,
    { seapointAccountId: string }
  >(
    xeroConnectionStatus?.connectionId
      ? `${API_URL_ACCOUNTING}${xeroConnectionStatus?.connectionId}/create-bank-account`
      : null,
    postSwrFetcher,
  );

  const [isXeroConnectionModalOpen, setIsXeroConnectionModalOpen] = useState(false);
  const [isOAuthProviderDisconnectModalOpen, setIsOAuthProviderDisconnectModalOpen] = useState(false);
  const [isAccountMappingExitConfirmationModalOpen, setIsAccountMappingExitConfirmationModalOpen] = useState(false);

  // Derive modal state directly from URL params - this is the better pattern (no useEffect needed)
  const isAccountMappingModalOpen = isXeroProvider && isXeroSuccess;

  // Only block on Xero details loading when we're fetching them (on Xero settings page)
  const isLoadingXeroDetails = shouldFetchXeroDetails && (isLoadingXeroBankAccounts || isLoadingXeroAccountMappings);

  if (isLoadingData || isCheckingXeroConnection || isCheckingGmailConnection || isLoadingXeroDetails) {
    return <LoadingSection text="Loading integrations..." />;
  }

  // Check if errors require OAuth reconnection
  const xeroRequiresReconnection =
    requiresOAuthReconnection(xeroBankAccountsError) || requiresOAuthReconnection(xeroAccountMappingsError);

  // If Xero requires reconnection, disconnect the stale connection and prompt user to reconnect
  if (
    xeroRequiresReconnection &&
    xeroConnectionStatus?.connected &&
    xeroConnectionStatus?.connectionId &&
    !isDisconnectingXero
  ) {
    // Disconnect the invalid connection in the background to sync backend state
    onDisconnectXero(xeroConnectionStatus.connectionId).catch((err) => {
      console.error('Failed to disconnect invalid Xero connection:', err);
      // Still show reconnect modal even if disconnect fails
    });
  }

  // After disconnection (or if already disconnected), open the connect modal
  if (xeroRequiresReconnection && !xeroConnectionStatus?.connected && !isXeroConnectionModalOpen) {
    setIsXeroConnectionModalOpen(true);
  }

  // Show generic error for other types of errors
  if (!data || !xeroConnectionStatus || !gmailConnectionStatus) {
    return <ErrorSection error={dataError} />;
  }

  // Don't show error page if it's just a reconnection issue - we'll show the modal instead
  if ((xeroBankAccountsError || xeroAccountMappingsError) && !xeroRequiresReconnection) {
    return <ErrorSection error={dataError} />;
  }

  const { accounts } = data;

  // Support Xero account mapping for all connected accounts (not just Modulr)
  const hasConnectedAccounts = accounts.length > 0;

  const connections: OAuthProviderConnection[] = [
    {
      id: OAuthProvider.GMAIL,
      status: getOAuthProviderConnectionStatus(gmailConnectionStatus),
    },
    {
      id: OAuthProvider.XERO,
      status: getOAuthProviderConnectionStatus(xeroConnectionStatus),
    },
    {
      id: 'quickbooks',
      status: 'COMING_SOON',
    },
    {
      id: 'slack',
      status: 'COMING_SOON',
    },
  ];

  const redirectUrlQueryParam = `redirectUrl=${encodeURIComponent(window.location.pathname)}`;

  const onConnectXero = async () => {
    window.location.href = `${CONNECT_XERO_URL}&${redirectUrlQueryParam}`;

    return;
  };

  const onCloseAccountMappingModal = () => {
    searchParams.delete('provider');
    searchParams.delete('success');
    searchParams.delete('accountId');
    setSearchParams(searchParams);
  };

  const providerConfig: ComponentProps<typeof IntegrationsPage>['providerConfig'] =
    oauthProvider && isAllowedOAuthProvider(oauthProvider)
      ? {
          provider: oauthProvider,
          connectionStatus: oauthProvider === OAuthProvider.XERO ? xeroConnectionStatus : gmailConnectionStatus,
          onGoBack: () => navigate('/integrations'),
          onDisconnect: () => setIsOAuthProviderDisconnectModalOpen(true),
          isRateLimited: oauthProvider === OAuthProvider.XERO ? isXeroBankAccountsRateLimited : undefined,
        }
      : { provider: undefined };

  const navigateToXeroConnectionPage = () => {
    navigate(XERO_CONNECTION_URL);
  };

  return (
    <>
      <IntegrationsPage
        connections={connections}
        providerConfig={providerConfig}
        onReviewInvoice={(connectionId) => {
          const status = connections.find((c) => c.id === connectionId)?.status;

          if (!status) {
            return;
          }

          switch (status) {
            case 'CONNECTED':
              navigate(`/integrations/${connectionId}`);

              return;
            case 'DISCONNECTED':
              if (connectionId === OAuthProvider.XERO) {
                setIsXeroConnectionModalOpen(true);
                return;
              }

              if (connectionId === OAuthProvider.GMAIL) {
                navigate('/invoice-pay?email-modal-open=integrations');
                return;
              }

              return;
            case 'COMING_SOON':
              return;
            default:
              return exhaustiveCheck(status);
          }
        }}
      />

      <XeroConnectModal
        isOpen={isXeroConnectionModalOpen}
        onClose={() => setIsXeroConnectionModalOpen(false)}
        onContinue={onConnectXero}
      />

      {oauthProvider === OAuthProvider.XERO && (
        <OAuthProviderDisconnectConfirmationModal
          provider={oauthProvider}
          isOpen={isOAuthProviderDisconnectModalOpen}
          isLoading={isDisconnectingXero}
          onClose={() => setIsOAuthProviderDisconnectModalOpen(false)}
          onDisconnect={async () => {
            if (!xeroConnectionStatus?.connectionId) {
              Sentry.captureMessage('No connection id found when trying to disconnect xero', {
                extra: {
                  xeroConnectionStatus,
                },
              });

              return;
            }

            try {
              await onDisconnectXero(xeroConnectionStatus?.connectionId);

              toast.success('Xero disconnected successfully');

              navigate('/integrations');
            } catch (error) {
              Sentry.captureException(error, {
                extra: {
                  error,
                  xeroConnectionStatus,
                  message: 'Failed to disconnect Xero from the integrations page',
                },
              });

              toast.error('Failed to disconnect Xero');
            }
          }}
        />
      )}

      {oauthProvider === OAuthProvider.GMAIL && (
        <OAuthProviderDisconnectConfirmationModal
          provider={oauthProvider}
          isOpen={isOAuthProviderDisconnectModalOpen}
          isLoading={isDisconnectingGmail}
          onClose={() => setIsOAuthProviderDisconnectModalOpen(false)}
          onDisconnect={async () => {
            if (!gmailConnectionStatus?.connectionId) {
              Sentry.captureMessage('No connection id found when trying to disconnect Gmail', {
                extra: {
                  gmailConnectionStatus,
                },
              });

              return;
            }

            try {
              await onDisconnectGmail(gmailConnectionStatus?.connectionId);

              toast.success('Gmail disconnected successfully');

              navigate('/integrations');
            } catch (error) {
              Sentry.captureException(error, {
                extra: {
                  error,
                  gmailConnectionStatus,
                  message: 'Failed to disconnect Gmail from the integrations page',
                },
              });

              toast.error('Failed to disconnect Gmail');
            }
          }}
        />
      )}

      {isAccountMappingModalOpen && xeroBankAccounts && xeroAccountMappings && hasConnectedAccounts && (
        <AccountMappingModal
          isOpen={isAccountMappingModalOpen}
          onClose={() => setIsAccountMappingExitConfirmationModalOpen(true)}
          accounts={accounts}
          xeroBankAccounts={xeroBankAccounts}
          xeroAccountMappings={xeroAccountMappings.mappings}
          preselectedAccountId={preselectedAccountId || undefined}
          isLoading={isMutatingXeroAccountMappings}
          isCreatingXeroAccount={isMutatingCreateXeroBankAccount}
          onCreateXeroAccount={async (seapointAccountId) => {
            try {
              const result = await triggerCreateXeroBankAccount({ seapointAccountId });

              // Optimistically update the bank accounts cache with the new account
              // This ensures the dropdown shows the new account immediately
              // Only add if a NEW account was created (not if an existing one was returned)
              const isNewAccount = result && !result.message.includes('existing');
              if (isNewAccount) {
                await mutateXeroBankAccounts(
                  (currentData) => {
                    if (!currentData) return currentData;
                    return {
                      ...currentData,
                      accounts: [
                        ...currentData.accounts,
                        {
                          id: result.xeroAccountId,
                          name: result.xeroAccountName,
                          currency: result.xeroAccountCurrency,
                        },
                      ],
                    };
                  },
                  // Don't revalidate - the optimistic update has the correct data from the create response
                  // Background revalidation could overwrite with stale cached data from the server
                  { revalidate: false },
                );
              }

              // Show appropriate message based on whether we created a new account or found an existing one
              if (isNewAccount) {
                toast.success('Xero bank account created successfully');
              } else {
                toast.success('Found existing Xero bank account');
              }

              return result?.xeroAccountId;
            } catch (error) {
              Sentry.captureException(error);

              const errorMessage =
                error instanceof AxiosError && error.response?.data?.message
                  ? `Failed to create Xero bank account. ${error.response.data.message}`
                  : 'Failed to create Xero bank account';
              toast.error(errorMessage);

              return undefined;
            }
          }}
          onSubmit={async (values) => {
            try {
              const sanitizedValues = values.map(
                (value) =>
                  ({
                    seapointAccountId: value.accountId,
                    externalAccountId: value.xeroAccountId,
                    syncFromDate: value.fromDate,
                  }) satisfies BankAccountMapping,
              );

              await triggerXeroAccountMappings({ mappings: sanitizedValues });

              toast.success('Account mappings updated successfully');

              onCloseAccountMappingModal();

              navigateToXeroConnectionPage();
            } catch (error) {
              Sentry.captureException(error);

              const errorMessage =
                error instanceof AxiosError && error.response?.data?.message
                  ? `Failed to update account mappings. ${error.response.data.message}`
                  : 'Failed to update account mappings';
              toast.error(errorMessage);
            }
          }}
        />
      )}

      <AccountMappingExitConfirmationModal
        isOpen={isAccountMappingExitConfirmationModalOpen}
        onClose={() => setIsAccountMappingExitConfirmationModalOpen(false)}
        onExit={() => {
          onCloseAccountMappingModal();

          setIsAccountMappingExitConfirmationModalOpen(false);

          navigateToXeroConnectionPage();
        }}
      />
    </>
  );
};

const getOAuthProviderConnectionStatus = (
  connectionStatus: OAuthConnectionInformation | null,
): Extract<OAuthConnectionStatus, 'CONNECTED' | 'DISCONNECTED'> => {
  if (!connectionStatus) {
    return 'DISCONNECTED';
  }

  const { connected } = connectionStatus;

  switch (connected) {
    case true:
      return 'CONNECTED';
    case false:
      return 'DISCONNECTED';
    default:
      return exhaustiveCheck(connected);
  }
};
